import React from 'react'
import HeadData from 'components/Common/HeadData/HeadData'
import ImageHero from 'components/Common/ImageHero/ImageHero'
import LayoutContainer from 'components/Common/LayoutContainer/LayoutContainer'
import Heading from 'components/Luxkit/Typography/Heading'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Pane from 'components/Common/Pane/Pane'
import Group from 'components/utils/Group'
import config from 'constants/config'
import { rem } from 'polished'
import PaneBody from 'components/Common/Pane/PaneBody'
import { BurnOptions, StepsHowItWorks } from './VFFLandingPageData'
import styled from 'styled-components'
import Caption from 'components/Luxkit/Typography/Caption'
import ResponsiveImage from 'components/Common/ResponsiveImage/ResponsiveImage'
import TextButton from 'components/Luxkit/Button/TextButton'
import VelocityOfferCarousel from './VelocityOfferCarousel'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import HttpStatus from 'components/Common/HttpStatus/HttpStatus'
import Error404Page from 'components/Pages/Error404Page'
import { connect } from 'react-redux'
import VFFStackedLogo from './VFFStackedLogo'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import VFFJoinPromo from './VFFJoinPromo'
import TextLink from 'components/Luxkit/TextLink'
import { getListName } from 'analytics/enhanced-ecommerce'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import { themeClassName } from 'lib/theme/themeUtils'
import { VELOCITY_ENABLED_REGIONS } from 'constants/config/region'

const HeroImage = styled(ImageHero)`
  max-height: ${rem(900)};
`

const TextOverlayContainer = styled(Group)`
  padding: ${rem(20)} ${rem(16)};
`

const StepBox = styled(Group)`
  padding: ${rem(20)};
  background: ${(props) => props.theme.palette.neutral.default.eight};
  border: ${(props) => `1px solid ${props.theme.palette.neutral.default.five}`};
  flex: 1;
`

const OptionBoxContent = styled(Group)`
  padding: ${rem(24)};
`

const OptionBox = styled(Group)`
  background: ${(props) => props.theme.palette.neutral.default.eight};
  border: ${(props) => `1px solid ${props.theme.palette.neutral.default.five}`};
  flex: 1;
`

interface Props {
  regionCode: string;
}

function VFFLandingPage({ regionCode }: Props) {
  const tracking = useOfferListTracking(getListName('OfferList', 'vff-landing-page', 'hotel-carousel'))

  const isMobileScreen = useScreenSizeOnly('mobile')

  if (!VELOCITY_ENABLED_REGIONS.includes(regionCode) || !config.VIRGIN_VELOCITY_ENABLED) {
    return <HttpStatus code={404}>
      <Error404Page invalidRegion />
    </HttpStatus>
  }

  return (
    <div>
      <HeadData
          title={`Use Velocity Points with ${config.title}`}
          description={`Make the most of your Velocity Frequent Flyer Points with ${config.title}. Use Velocity Points for all or a part of your holiday booking.`}
        />

      <HeroImage
        alt="Velocity Frequent Flyer"
        id={isMobileScreen ? 'velocityHeroImage' : 'velocityHeroImageV3' }
        gravity="auto"
        brightness={isMobileScreen ? -40 : 0}
      >
        <TextOverlayContainer direction="vertical" className={themeClassName('inverse')} gap={8}>
          <Group direction="vertical" gap={24} tabletGap={40}>
            <Group direction="vertical" horizontalAlign="center" fullWidth>
              <VFFStackedLogo width={isMobileScreen ? 160 : 200} height={isMobileScreen ? 60 : 75} />
            </Group>

            <Group direction="vertical" gap={4} tabletGap={8}>
              <Heading variant="heading2" align="center">
                The world's best escapes, now with Velocity Points
              </Heading>
              <BodyText variant="large" align="center">
                Login to your Velocity account at checkout to use Velocity Points on eligible escapes
              </BodyText>
            </Group>
            <Group direction="vertical" gap={20} tabletGap={40} horizontalAlign="center">
              <TextButton kind="primary" size="large" to="/">
                View offers
              </TextButton>
            </Group>
          </Group>

        </TextOverlayContainer>
      </HeroImage>
      <Pane>
        <PaneBody size="xl-y">
          <LayoutContainer>
            <Group direction="vertical" gap={20}>
              <Heading variant="heading3" align="center">
                How to Use Velocity Points with Luxury Escapes
              </Heading>

              <Group tabletDirection="horizontal" direction="vertical" gap={20} fullWidth verticalAlign="stretch">
                {StepsHowItWorks.map((step, index) => (
                  <StepBox key={index} direction="vertical" gap={12} horizontalAlign="center">
                    {step.icon}
                    <Heading variant="heading5" align="center">
                      {step.title}
                    </Heading>
                    <BodyText variant="medium" align="center">
                      {step.description}
                    </BodyText>
                  </StepBox>
                ))}
              </Group>
            </Group>
          </LayoutContainer>
        </PaneBody>
        <PaneBody size="xl-y">
          <LayoutContainer>
            <VFFJoinPromo />
          </LayoutContainer>
        </PaneBody>
        <PaneBody size="xl-y">
          <LayoutContainer>
            <Group direction="vertical" gap={20}>
              <Heading variant="heading3" align="center">
                Let's get to the <i>Point(s)</i>
              </Heading>
              <Group direction="vertical" gap={24} tabletGap={32}>
                <BodyText variant="large" align="center">
                  With Luxury Escapes and Velocity Frequent Flyer, great value takes off. Members can now use Velocity Points on the world's best escapes, handpicked by the experts.
                </BodyText>
                <Group tabletDirection="horizontal" direction="vertical" gap={24} tabletGap={32} fullWidth verticalAlign="stretch">
                  {BurnOptions.map((option, index) => (
                    <OptionBox direction="vertical" tabletDirection="horizontal" key={index}>
                      <ResponsiveImage
                        alt="Velocity Frequent Flyer"
                        id={option.image}
                        gravity="center"
                        mobileWidth="500"
                        mobileAspectRatio="16:9"
                      />
                      <OptionBoxContent direction="vertical" gap={12} horizontalAlign="start" fullHeight>
                        <Group direction="vertical" verticalAlign="space-between" horizontalAlign="start" gap={16} fullHeight>
                          <Group direction="vertical" verticalAlign="start">
                            <Heading variant="heading5" align="start">
                              {option.title}
                            </Heading>
                            <Group direction="vertical" gap={12}>
                              <Caption variant="large" align="start">
                                {option.caption}
                              </Caption>
                              <BodyText variant="medium" align="start">
                                {option.description}
                              </BodyText>
                            </Group>
                          </Group>

                          <TextButton
                            kind="primary"
                            size="large"
                            to="/">
                            {option.buttonText}
                          </TextButton>
                        </Group>
                      </OptionBoxContent>
                    </OptionBox>
                  ))}
                </Group>
              </Group>
            </Group>
          </LayoutContainer>
        </PaneBody>
        <OfferListEventsProvider tracking={tracking}>
          <VelocityOfferCarousel />
        </OfferListEventsProvider>
        <PaneBody size="xl-y">
          <LayoutContainer>
            <Group direction="horizontal" verticalAlign="center" horizontalAlign="start" gap={8}>
              <TextLink size="small" weight="regular" to="/frequent-flyer-programs">
                Terms & Conditions
              </TextLink>
              <BodyText variant="large">
                &middot;
              </BodyText>
              <TextLink size="small" weight="regular" to="/frequent-flyer-programs#frequently-asked-questions">
                FAQ
              </TextLink>
            </Group>
          </LayoutContainer>
        </PaneBody>
      </Pane>
    </div>
  )
}

const mapStateToProps = (state: App.State) => ({
  regionCode: state.geo.currentRegionCode,
})

export default connect(mapStateToProps)(VFFLandingPage)
